/**
 * FormPage — слот «Форма»: состояние организма СЕЙЧАС.
 *
 * ГРАНИЦА ЭКРАНА (MOBILE_NAV_ARCHITECTURE.md раздел 3). Здесь только текущее
 * значение и короткий тренд. Всё, что требует изучения рядов, живёт в
 * «Анализе». Границу легко размыть — и тогда экран, который должен
 * читаться за пять секунд утром, превращается во второй аналитический
 * раздел. Каждый блок ниже отвечает на вопрос «можно ли сегодня работать»,
 * и ничего сверх того.
 *
 * ЧЕСТНОСТЬ ЦИФР. Ни одно значение не выдумывается: нет данных — стоит «—»,
 * а не ноль и не заглушка. Порог показывается просроченным только когда
 * бэкенд прислал `overdue` (ThresholdProtocolStatus), а не когда нам
 * показалось по дате.
 */
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaretRight, Warning } from "@phosphor-icons/react";

import { usePmcSeries, useHrvBaseline } from "../athleteMetricsApi";
import { useLatestReadiness, useWellnessList } from "../wellnessApi";
import { useSportMetrics } from "../zonesApi";
import { useThresholdTestsHistory } from "../analyticsApi";
import { useInjuries } from "../injuriesApi";
import { useGoal } from "../goalApi";
import { usePlan } from "../planApi";
import { readinessWorkoutAdvice } from "../readinessAdvice";
import { useLocalStorage } from "../hooks/useLocalStorage";
import {
  ANALYSIS_DISC_KEY,
  athleteDisciplines,
  resolveDisc,
  studioLink,
  type AnalysisDiscipline,
  type StoredDiscipline,
} from "../analysisPrefs";
import { freshnessState } from "../lib/freshnessScale";
import { metricAgeDays } from "../lib/metricAge";
import { latestMetric } from "../lib/wellnessLatest";
import { Skeleton } from "../components/Skeleton";
import { Sparkline } from "../components/canon/Sparkline";
import { ScrubbableSparkline } from "../components/chart/ScrubbableSparkline";
import { Card } from "../components/ui/Card";
import { formatPaceSec } from "../lib/time";
import { isoDate } from "../dateUtils";

const DASH = "—";

/**
 * Подписи дисциплин — исчерпывающей картой, а не цепочкой тернарок.
 *
 * Тернарка с общим «иначе» здесь уже соврала: пока видов было три, ветка по
 * умолчанию отдавала «Бег», и добавление силовой назвало бы её бегом — молча,
 * без единой ошибки компилятора. `Record` по типу дисциплины заставляет
 * дописать подпись при каждом новом виде.
 */
const DISC_LABEL: Record<AnalysisDiscipline, [key: string, fallback: string]> = {
  all: ["form_page.disc_all", "Всё"],
  swim: ["form_page.swim", "Плавание"],
  ride: ["form_page.ride", "Вело"],
  run: ["form_page.run", "Бег"],
  strength: ["form_page.strength", "Сила"],
  hyrox: ["form_page.hyrox", "Hyrox"],
};

/**
 * Запасные формулировки свежести — по ступеням общей шкалы (lib/freshnessScale).
 * Ступень одна на всё приложение, а длина фразы разная по месту: здесь строка
 * под мини-графиком, в линзе — абзац.
 */
const FRESH_FALLBACK: Record<ReturnType<typeof freshnessState>, string> = {
  deep: "Глубокая усталость — сначала разгрузка",
  tired: "Усталость накоплена — следи за восстановлением",
  neutral: "Нагрузка и восстановление уравновешены",
  race: "Свежесть в коридоре к старту",
  detrained: "Свежести больше, чем нужно — пора возвращать нагрузку",
};

/** Значение или честный прочерк — никогда не ноль вместо «нет данных». */
function show(v: number | null | undefined, digits = 0): string {
  return v == null ? DASH : v.toFixed(digits);
}

/**
 * Число, скелетон или прочерк — три разных состояния одной цифры.
 *
 * Прочерк отвечает «мы измеряли и не знаем», скелетон — «ещё спрашиваем». До
 * 2026-08-04 оба состояния выглядели прочерком, и на холодной загрузке экран
 * утверждал, что готовности и свежести нет, за секунду до того, как показать их.
 */
function Figure({
  loading,
  value,
  digits = 0,
  className,
  skeletonClass,
}: {
  loading: boolean;
  value: number | null | undefined;
  digits?: number;
  className?: string;
  skeletonClass: string;
}) {
  if (loading && value == null) return <Skeleton className={skeletonClass} />;
  return <span className={className}>{show(value, digits)}</span>;
}

/** Ряд последних значений метрики, старые → новые, без пропусков. */
function series(rows: { date: string }[] | undefined, key: string): number[] {
  if (!rows) return [];
  return rows
    .map((r) => (r as unknown as Record<string, number | null>)[key])
    .filter((v): v is number => typeof v === "number");
}

/**
 * Последнее ИЗВЕСТНОЕ значение метрики и день, когда оно измерено.
 *
 * ═══ ПОЧЕМУ НЕ ПРОСТО ПОСЛЕДНЯЯ СТРОКА — 2026-07-29 ═══
 *
 * `/athlete/wellness` отдаёт непрерывный ряд дней по возрастанию, включая
 * СЕГОДНЯШНИЙ — а он пуст до тех пор, пока атлет не заполнил самочувствие
 * или не прошла синхронизация. Взятая в лоб `rows[rows.length - 1]` — это
 * именно сегодня, то есть строка из одних null: экран показывал прочерки
 * в «Сне», «Пульсе покоя» и «Весе» при том, что вчерашние данные были на
 * месте, а спарклайн под прочерком рисовался по всему ряду.
 *
 * Метрика ищется отдельно от остальных, потому что заполняются они в разном
 * ритме: сон приходит каждую ночь, вес — раз в несколько дней. Общая
 * «последняя полная строка» выбросила бы свежий сон ради того, чтобы совпасть
 * с давним взвешиванием.
 */
// Реализация переехала в lib/wellnessLatest — 2026-08-03: «Сегодня» читал тот
// же ряд с НАЧАЛА и показывал HRV двухнедельной давности. Пока хелпер жил
// здесь, у второго экрана не было шанса им воспользоваться.
const latest = latestMetric;

/** Сколько дней назад измерено — чтобы не выдавать позавчерашний вес за сегодняшний. */
function daysAgo(date: string | null): number | null {
  if (!date) return null;
  const then = new Date(`${date}T00:00:00`).getTime();
  const today = new Date(isoDate(new Date())).getTime();
  if (Number.isNaN(then)) return null;
  return Math.round((today - then) / 86_400_000);
}

interface TileProps {
  label: string;
  value: string;
  unit?: string;
  values: number[];
  hint?: string;
  to?: string;
  testId: string;
  /**
   * Ответ ещё не пришёл. Прочерк в плитке значит «измеряли и не знаем» — на
   * загрузке он врал бы теми же символами, какими в «Весе» говорит правду.
   */
  loading?: boolean;
  /**
   * Базовый уровень метрики, если он ИЗМЕРЕН, а не придуман.
   *
   * Плитка писала «база 57» словами, но на линии этой отметки не было —
   * увидеть, что сегодня ниже коридора, было нечем. Базу рисуем только там,
   * где её считает бэкенд: медиана ряда, выданная за норму, выглядела бы
   * таким же измерением и была бы им не больше, чем среднее по больнице.
   */
  baseline?: number;
}

/** Плитка метрики: текущее крупно, тренд линией, подпись словами. */
function MetricTile({ label, value, unit, values, hint, to, testId, baseline, loading }: TileProps) {
  const { t } = useTranslation("nav");
  const pending = loading && value === DASH;
  const body = (
    <>
      <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-fg-muted">
        {label}
      </div>
      <div className="flex items-baseline gap-1 mt-1">
        {pending ? (
          <Skeleton className="h-[18px] w-[46px]" />
        ) : (
          <span className="text-[20px] leading-none text-fg-primary num-display">{value}</span>
        )}
        {unit && !pending && <span className="text-[11px] text-fg-muted">{unit}</span>}
      </div>
      {values.length >= 2 && (
        <div className="mt-2">
          {/* Прощупываемый: провёл пальцем — увидел значение за нужный день,
              не уходя с экрана. Плитка целиком остаётся ссылкой, но click
              после проведения гасится — иначе взгляд на среду уводил бы в
              другой раздел. */}
          <ScrubbableSparkline
            values={values}
            stroke="var(--fg-secondary)"
            width={96}
            height={20}
            baseline={baseline}
            showLatestDot
            ariaLabel={t("form_page.tile_trend_aria", { defaultValue: "{{label}}: тренд", label })}
          />
        </div>
      )}
      {hint && <div className="text-[10.5px] text-fg-muted mt-1.5 leading-snug">{hint}</div>}
    </>
  );

  const cls = "indigo-card px-3 py-2.5 block";
  return to ? (
    <Link to={to} data-testid={testId} className={`${cls} hover:bg-bg-raised active:bg-bg-raised transition-colors`}>
      {body}
    </Link>
  ) : (
    <div data-testid={testId} className={cls}>{body}</div>
  );
}

export function FormPage() {
  const { t } = useTranslation("nav");
  // Готовность больше не берётся из дашборда: тот отвечает строго за
  // сегодня и молчит до утренней синхронизации. «Форме» нужна последняя
  // известная точка с датой — см. useLatestReadiness ниже.
  //
  // ЗАГРУЗКА И ПУСТОТА — РАЗНЫЕ СОСТОЯНИЯ (2026-08-04). Прочерк в этом файле
  // означает «данных нет» и специально не заменяется нулём. Но пока ответ не
  // пришёл, тот же прочерк стоял и на месте существующих чисел: на холодной
  // загрузке «Готовность» и все три числа свежести показывали «—», а через
  // секунду становились 62 / 30 / 47 / −17. Прочерк говорил неправду — и
  // ровно тем же знаком, каким в соседней плитке говорит правду про вес.
  const { data: pmc, isLoading: pmcLoading } = usePmcSeries(6);
  // Свой isLoading: плитка HRV раньше слушала только wellness-запрос и на
  // холодной загрузке показывала прочерк, пока /metrics/hrv ещё летел.
  const { data: hrv, isLoading: hrvLoading } = useHrvBaseline(14);
  const { data: wellness, isLoading: wellnessLoading } = useWellnessList(14);
  const { data: sport, isLoading: sportLoading } = useSportMetrics();
  const { data: readiness, isLoading: readinessLoading } = useLatestReadiness();
  // Травмы: ноль записей — это «травм нет», а не «мы не знаем».
  const { data: injuries } = useInjuries();
  // Сегодняшняя тренировка — чтобы готовность отвечала не в пустоту, а
  // применительно к тому, что предстоит. Окно в один день: «Форма» не
  // показывает план, ей нужен только сегодняшний повод.
  // Последняя точка кривой: нужна и карточке свежести ниже, и совету по
  // сегодняшней тренировке — поэтому считается до обоих.
  const ctl = pmc?.ctl?.length ? pmc.ctl[pmc.ctl.length - 1] : null;
  const atl = pmc?.atl?.length ? pmc.atl[pmc.atl.length - 1] : null;
  const tsb = pmc?.tsb?.length ? pmc.tsb[pmc.tsb.length - 1] : null;

  const todayIso = useMemo(() => isoDate(new Date()), []);
  const { data: todayPlan } = usePlan(todayIso, todayIso);

  // Дисциплинарный контекст — общий с «Анализом» (один ключ хранения,
  // ARCHITECTURE раздел 3: «запоминается для Анализа»). Правило 4: смена цели
  // сбрасывает фильтр — за это отвечает resolveDisc.
  const { data: goal } = useGoal();
  const goalId = goal?.id ?? null;
  const [storedDisc, setStoredDisc] = useLocalStorage<StoredDiscipline | null>(ANALYSIS_DISC_KEY, null);
  const disc = resolveDisc(storedDisc, goalId);
  // Выбор атлета ПЛЮС факт: профиль знал только бег при девятнадцати
  // велотренировках за полгода, и вело-порог не показывался вовсе.
  const discs = athleteDisciplines(sport);
  // Переключатель показываем от двух дисциплин — одной фильтровать нечего.
  const showDiscSwitcher = discs.length >= 2;
  const discMatches = (d: Exclude<AnalysisDiscipline, "all">) => disc === "all" || disc === d;

  // Пороги: окно в год — тесты редкие, за месяц их может не быть вовсе.
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 365 * 86_400_000);
    return { from: isoDate(from), to: isoDate(to) };
  }, []);
  const { data: thresholds } = useThresholdTestsHistory(range);

  const rows = wellness ?? [];

  // Каждая метрика ищет своё последнее известное значение — ряд включает
  // сегодняшний день, который пуст до заполнения (см. latest()).
  const sleepLast = latest(rows, "sleep_hours");
  const rhrLast = latest(rows, "rhr");
  const weightLast = latest(rows, "body_weight");

  // Сегодняшняя тренировка и предупреждение по ней. Совет молчит сам, если
  // день лёгкий или готовность в порядке — на экране остаётся просто строка
  // «что сегодня».
  const todayWorkout = todayPlan?.items?.[0] ?? null;
  const advice = readinessWorkoutAdvice({
    readinessScore: readiness?.score ?? null,
    // Свежесть — второй сигнал, не поправка к первому: выспаться в глубокой
    // яме можно, выйти из неё за ночь нельзя. У Димы готовность 62 (норма) при
    // TSB −17 (глубокая усталость) и пороговой работой в плане — совет молчал.
    tsb,
    todayIso,
    workout: todayWorkout
      ? {
          status: todayWorkout.status,
          scheduled_date: todayWorkout.scheduled_date,
          is_key_workout: todayWorkout.is_key_workout ?? false,
          // Зона из структуры работы: у плана, приехавшего импортом, остальные
          // признаки тяжести пусты все до одного.
          max_zone: todayWorkout.max_zone ?? null,
          intensity_factor: null,
          planned_load: todayWorkout.planned_load ?? null,
        }
      : null,
  });

  /** «2 дня назад» — если значение не сегодняшнее и не вчерашнее. */
  const staleHint = (date: string | null): string | undefined => {
    const d = daysAgo(date);
    if (d == null || d <= 1) return undefined;
    return t("form_page.measured_days_ago", {
      defaultValue: `{{count}} дн. назад`,
      count: d,
    });
  };


  // Свежесть словами вместо формулы: канон запрещает показывать расчёт,
  // но обязывает объяснить смысл.
  //
  // СТУПЕНЬ — ИЗ ОБЩЕЙ ШКАЛЫ (2026-08-04). Здесь стояли свои ТРИ ступени со
  // своими порогами (+5 / −10), четвёртая копия шкалы свежести в приложении и
  // единственная, у которой границы отличались от остальных: при TSB −6 эта
  // карточка говорила «в рабочем коридоре», а плитка в Анализе про то же
  // число — «усталость». Формулировки остаются свои (здесь нужна короткая
  // строка, в линзе — абзац), но ступень теперь одна на всех.
  const freshnessHint = useMemo(() => {
    if (tsb == null) return undefined;
    const state = freshnessState(tsb);
    // defaultValue обязателен, как и у остальных ключей этого файла: без него
    // отсутствующий перевод не падает, а тихо отдаёт саму строку ключа —
    // «form_page.fresh_tired» на экране вместо человеческой фразы.
    return t(`form_page.fresh_${state}`, { defaultValue: FRESH_FALLBACK[state] });
  }, [tsb, t]);

  /**
   * Подсказка HRV: база и СТОРОНА от неё.
   *
   * Одно число базы отвечает «сколько обычно» и молчит о том, ради чего
   * метрику открывают утром, — где я сейчас относительно обычного. Порог в
   * пять процентов взят как ширина шума измерения: колебание внутри него
   * называть отклонением значило бы будить тревогу на пустом месте.
   */
  const hrvHint = useMemo(() => {
    if (hrv?.baseline == null) return undefined;
    const base = Math.exp(hrv.baseline);
    const last = hrv.values?.length ? Math.exp(hrv.values[hrv.values.length - 1]) : null;
    const baseText = t("form_page.hrv_baseline", {
      defaultValue: `база ${Math.round(base)}`,
      base: Math.round(base),
    });
    if (last == null || base <= 0) return baseText;
    const off = (last - base) / base;
    if (off < -0.05) return `${baseText} · ${t("form_page.hrv_below", { defaultValue: "ниже обычного" })}`;
    if (off > 0.05) return `${baseText} · ${t("form_page.hrv_above", { defaultValue: "выше обычного" })}`;
    return `${baseText} · ${t("form_page.hrv_at", { defaultValue: "в норме" })}`;
  }, [hrv, t]);

  /**
   * Состояние разделов «Тела» — то, ради чего в них заходят.
   *
   * Самочувствие: когда последняя запись. Пустой сегодняшний день — норма до
   * утренней синхронизации, поэтому ищем последнюю ЗАПОЛНЕННУЮ (тот же
   * `latest`, что у плиток).
   */
  const wellnessState = useMemo((): { text: string; tone?: "warn" } | undefined => {
    const last = latest(rows, "hrv").date ?? latest(rows, "sleep_hours").date ?? null;
    const d = daysAgo(last);
    if (d == null) return undefined;
    if (d === 0) return { text: t("form_page.state_today", { defaultValue: "сегодня" }) };
    if (d === 1) return { text: t("form_page.state_yesterday", { defaultValue: "вчера" }) };
    return {
      text: t("form_page.state_days_ago", { defaultValue: "{{n}} дн. назад", n: d }),
      // Больше недели без данных — это уже не «свежо»: от них зависит
      // готовность, которой открывается экран.
      tone: d > 7 ? "warn" : undefined,
    };
  }, [rows, t]);

  /**
   * Травмы: НОЛЬ ЗАПИСЕЙ — ЭТО ОТВЕТ, а не отсутствие ответа. «Травм нет» —
   * то, что атлет хочет видеть, не открывая раздел; открытая травма должна
   * называться вслух вместе со степенью восстановления.
   */
  const injuryState = useMemo((): { text: string; tone?: "warn" } | undefined => {
    if (injuries == null) return undefined;
    const open = injuries.filter((i) => i.is_open);
    if (open.length === 0) {
      return { text: t("form_page.injuries_none", { defaultValue: "нет активных" }) };
    }
    const first = open[0];
    const pct = first.recovery_pct;
    return {
      text: pct != null
        ? t("form_page.injuries_one_pct", {
            defaultValue: "{{part}} · {{pct}}%",
            part: first.body_part,
            pct,
          })
        : first.body_part,
      tone: "warn",
    };
  }, [injuries, t]);

  /**
   * Свежесть профильного веса. От него считаются Вт/кг, и «из профиля» без
   * срока звучало как «актуально»: у Димы это 90 кг без даты в metric_sources
   * при последнем взвешивании в дневнике за восемь месяцев до замера.
   */
  const weightAgeHint = useMemo(() => {
    const fromProfile = t("form_page.weight_from_profile", { defaultValue: "из профиля" });
    const d = daysAgo(sport?.weight_measured_at ?? null);
    if (d == null) return fromProfile;
    if (d < 31) {
      return `${fromProfile} · ${t("form_page.age_days", { defaultValue: "{{n}} дн.", n: d })}`;
    }
    const months = Math.round(d / 30);
    return `${fromProfile} · ${t("form_page.age_months", { defaultValue: "{{n}} мес.", n: months })}`;
  }, [sport?.weight_measured_at, t]);

  const overdue = (thresholds?.protocols ?? []).filter((p) => p.overdue);

  /**
   * Возраст порога словами. Показываем ТОЛЬКО когда дата известна: выдуманная
   * свежесть хуже её отсутствия. У Димы дата есть (`metric_sources`, ab05) — и
   * она говорит, что FTP и CSS приехали из intervals два месяца назад, чего на
   * экране до сих пор не было видно.
   */
  const thresholdAge = useCallback(
    (field: string): string | undefined => {
      const days = metricAgeDays(sport?.metric_sources ?? null, field);
      if (days == null) return undefined;
      if (days === 0) return t("form_page.age_today", { defaultValue: "сегодня" });
      if (days < 31) {
        return t("form_page.age_days", { defaultValue: "{{n}} дн.", n: days });
      }
      const months = Math.round(days / 30);
      return t("form_page.age_months", { defaultValue: "{{n}} мес.", n: months });
    },
    [sport?.metric_sources, t],
  );

  return (
    <div className="p-3 space-y-4" data-testid="form-page">

      {/* 1 · Готовность — первое, ради чего экран открывают утром */}
      <Card className="px-4 py-3.5" data-testid="form-readiness">
        <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-fg-muted">
          {t("form_page.readiness", { defaultValue: "Готовность" })}
        </div>
        <div className="flex items-baseline gap-2 mt-1">
          {/* Правило 1: готовность ведёт к своему объяснению — Анализ/Здоровье */}
          <Link
            to={studioLink("health", disc)}
            data-testid="form-readiness-link"
            className="text-[34px] leading-none text-fg-primary num-display"
          >
            <Figure
              loading={readinessLoading}
              value={readiness?.score ?? null}
              skeletonClass="h-[30px] w-[52px]"
            />
          </Link>
          {sleepLast.value != null && (
            <span className="text-[11.5px] text-fg-muted">
              {t("form_page.sleep_short", { defaultValue: "сон" })} {sleepLast.value.toFixed(1)}
            </span>
          )}
        </div>

        {/* За КАКОЙ день число — 2026-08-03. «Форма» показывает линию, и до
            утренней синхронизации выводит вчерашнюю точку: это законно ровно
            до тех пор, пока дата названа вслух. Молча подставленное вчерашнее
            — то же враньё, что HRV двухнедельной давности на «Сегодня». */}
        {readiness?.score != null && !readiness.is_today && readiness.days_ago != null && (
          <div className="text-[10.5px] text-fg-muted mt-1" data-testid="form-readiness-asof">
            {t("form_page.readiness_as_of", {
              defaultValue: "по данным за {{count}} дн. назад",
              count: readiness.days_ago,
            })}
          </div>
        )}

        {/* Готовность применительно к тому, что предстоит. Раньше «свежесть
            −23» и «сегодня пороговые» стояли на экране двумя фактами рядом,
            и сводить их приходилось человеку. */}
        {todayWorkout && (
          <div className="mt-2.5 text-[12px]" data-testid="form-today-workout">
            <span className="text-fg-muted">
              {t("form_page.today_is", { defaultValue: "Сегодня" })}:{" "}
            </span>
            <Link to={`/workout/${todayWorkout.id}`} className="text-fg-primary hover:underline">
              {todayWorkout.title ?? t("form_page.workout_fallback", { defaultValue: "тренировка" })}
            </Link>
            {advice && (
              <div className="text-[11.5px] text-warn mt-1" data-testid="form-readiness-advice">
                {/* Причина названа вслух: «не выспался» и «в яме третью неделю»
                    требуют разных решений, а одна фраза на оба случая
                    объясняла бы только первый. */}
                {advice.reason === "deep_fatigue"
                  ? t("form_page.advice_deep_fatigue", {
                      defaultValue:
                        "Тяжёлый день на глубокой усталости — сначала разгрузка, потом интенсивность.",
                    })
                  : t("form_page.advice_reduce", {
                      defaultValue:
                        "Готовность ниже обычного для такого дня — стоит снизить интенсивность или перенести.",
                    })}
              </div>
            )}
          </div>
        )}

        <Link
          to="/wellness/checkin"
          data-testid="form-checkin-link"
          className="mt-3 inline-flex items-center gap-1 text-[12px] text-fg-secondary hover:text-fg-primary transition-colors"
        >
          {t("form_page.checkin", { defaultValue: "Отметить самочувствие" })}
          <CaretRight className="w-3 h-3" aria-hidden />
        </Link>
      </Card>

      {/* Дисциплина — общий контекст с Анализом (от двух активных видов) */}
      {showDiscSwitcher && (
        <div
          className="flex gap-1.5 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
          role="group"
          aria-label={t("form_page.disc_aria", { defaultValue: "Дисциплина" })}
          data-testid="form-disc-switcher"
        >
          {(["all", ...discs] as AnalysisDiscipline[]).map((d) => {
            const active = disc === d;
            const [labelKey, fallback] = DISC_LABEL[d];
            const label = t(labelKey, { defaultValue: fallback });
            return (
              <button
                key={d}
                type="button"
                onClick={() => setStoredDisc({ disc: d, goalId })}
                aria-pressed={active}
                data-testid={`form-disc-${d}`}
                className={`shrink-0 h-7 px-3 rounded-full text-[11px] font-mono uppercase tracking-[0.06em] press-scale ${
                  active
                    ? "bg-accent text-accent-on font-semibold"
                    : "bg-bg-card text-fg-muted shadow-[var(--shadow-card)]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* 2 · Свежесть — три числа и одна фраза, ряды живут в Анализе */}
      <Card className="px-4 py-3.5" data-testid="form-freshness">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-fg-muted">
            {t("form_page.freshness", { defaultValue: "Свежесть" })}
          </div>
          <Link
            to={studioLink("form", disc)}
            data-testid="form-to-analysis"
            className="text-[11px] text-fg-muted hover:text-fg-primary transition-colors"
          >
            {t("form_page.open_analysis", { defaultValue: "Разобрать" })} ›
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <div>
            <div className="text-[10px] text-fg-muted">{t("form_page.ctl", { defaultValue: "Форма" })}</div>
            <div className="text-[19px] text-fg-primary num-display leading-none mt-0.5"><Figure loading={pmcLoading} value={ctl} skeletonClass="h-[17px] w-[34px]" /></div>
          </div>
          <div>
            <div className="text-[10px] text-fg-muted">{t("form_page.atl", { defaultValue: "Усталость" })}</div>
            <div className="text-[19px] text-fg-primary num-display leading-none mt-0.5"><Figure loading={pmcLoading} value={atl} skeletonClass="h-[17px] w-[34px]" /></div>
          </div>
          <div>
            <div className="text-[10px] text-fg-muted">{t("form_page.tsb", { defaultValue: "Свежесть" })}</div>
            <div className="text-[19px] text-fg-primary num-display leading-none mt-0.5"><Figure loading={pmcLoading} value={tsb} skeletonClass="h-[17px] w-[34px]" /></div>
          </div>
        </div>
        {/* Той же высоты, что спарклайн ниже: числа выше уже со скелетонами,
            а график впрыгивал на 38px секундой позже — карточка дёргалась.
            isLoading истинен только без данных (v5): при фоновом refetch
            скелетон не накрывает живой график. */}
        {pmcLoading && (
          <Skeleton className="h-[38px] w-full mt-2.5" />
        )}
        {pmc?.ctl && pmc.ctl.length >= 2 && (
          <div className="mt-2.5">
            <Sparkline
              values={pmc.ctl}
              stroke="var(--accent)"
              fill="var(--accent)"
              width={280}
              height={38}
              showLatestDot
              ariaLabel={t("form_page.ctl_trend", { defaultValue: "Тренд формы" })}
            />
          </div>
        )}
        {freshnessHint && (
          <div className="text-[11px] text-fg-muted mt-2 leading-snug">{freshnessHint}</div>
        )}
      </Card>

      {/* 3 · Организм — четыре плитки с трендом за 14 дней */}
      <section data-testid="form-body-metrics">
        <h2 className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted mb-2 px-0.5">
          {t("form_page.organism", { defaultValue: "Организм" })}
        </h2>
        <div className="grid grid-cols-2 gap-2">
          <MetricTile
            testId="form-tile-hrv"
            loading={wellnessLoading || hrvLoading}
            label={t("form_page.hrv", { defaultValue: "HRV" })}
            // Эндпоинт отдаёт lnRMSSD (compute_lnrmssd_series) — атлету
            // показываем миллисекунды: «HRV 4» читается как показание
            // прибора и пугает, «HRV 57» — узнаваемое значение.
            value={show(
              hrv?.values?.length ? Math.exp(hrv.values[hrv.values.length - 1]) : null,
            )}
            values={(hrv?.values ?? []).map((v) => Math.exp(v))}
            baseline={hrv?.baseline != null ? Math.exp(hrv.baseline) : undefined}
            hint={hrvHint}
            to={studioLink("health", disc)}
          />
          <MetricTile
            testId="form-tile-sleep"
            loading={wellnessLoading}
            label={t("form_page.sleep", { defaultValue: "Сон" })}
            value={show(sleepLast.value, 1)}
            unit={t("form_page.hours", { defaultValue: "ч" })}
            values={series(rows, "sleep_hours")}
            hint={staleHint(sleepLast.date)}
            to={studioLink("health", disc)}
          />
          <MetricTile
            testId="form-tile-rhr"
            loading={wellnessLoading}
            label={t("form_page.rhr", { defaultValue: "Пульс покоя" })}
            value={show(rhrLast.value)}
            values={series(rows, "rhr")}
            hint={staleHint(rhrLast.date)}
            to={studioLink("health", disc)}
          />
          {/* ВЕС ЖИВЁТ В ДВУХ МЕСТАХ — 2026-08-04.
              Плитка читала только дневниковый `body_weight` и показывала
              прочерк, хотя в профиле стояли 90 кг: у Димы дневник веса пуст
              все 14 дней. Прочерк там означал «мы не знаем», а знали. Дневник
              остаётся главным (он даёт динамику и спарклайн), профиль —
              подстраховка с честной пометкой, откуда число. */}
          <MetricTile
            testId="form-tile-weight"
            loading={wellnessLoading}
            label={t("form_page.weight", { defaultValue: "Вес" })}
            value={
              weightLast.value != null
                ? show(weightLast.value, 1)
                : show(sport?.weight_kg ?? null, 1)
            }
            values={series(rows, "body_weight")}
            hint={
              weightLast.value != null
                ? staleHint(weightLast.date)
                : sport?.weight_kg != null
                  ? weightAgeHint
                  : undefined
            }
            to={studioLink("health", disc)}
          />
        </div>
      </section>

      {/* 4 · Пороги по дисциплинам — на них стоит всё планирование зон */}
      <section data-testid="form-thresholds">
        <h2 className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted mb-2 px-0.5">
          {t("form_page.thresholds", { defaultValue: "Пороги" })}
        </h2>
        <Card className="divide-y divide-bg-divider">
          {/* Пока профиль летит, карточка держит две строки-скелетона: раньше
              она сжималась до ссылки «Мои зоны», и пороги впрыгивали сверху,
              сдвигая всё под собой. Две — типичный минимум (порог вида +
              пульсовый), угадывать точнее нечем. */}
          {sportLoading && !sport &&
            [0, 1].map((i) => (
              <div key={i} className="flex items-center justify-between px-3.5 py-3" aria-hidden="true">
                <Skeleton className="h-[13px] w-[76px]" />
                <Skeleton className="h-[13px] w-[92px]" />
              </div>
            ))}
          {/* Правило 2: порог ведёт к профилю своего вида. Фильтр дисциплины
              прячет чужие строки — пульсовый порог общий, виден всегда. */}
          {sport?.css_sec_per_100m != null && discMatches("swim") && (
            <ThresholdRow
              testId="form-threshold-swim"
              label={t("form_page.swim", { defaultValue: "Плавание" })}
              value={`${formatPaceSec(sport.css_sec_per_100m)} ${t("form_page.per_100m", { defaultValue: "/100м" })}`}
              age={thresholdAge("css_sec_per_100m")}
              to="/sport-profile/swim"
            />
          )}
          {sport?.ftp_watts != null && discMatches("ride") && (
            <ThresholdRow
              testId="form-threshold-ride"
              label={t("form_page.ride", { defaultValue: "Вело" })}
              // Вт/кг рядом с ваттами: 290 Вт сами по себе ни с чем не
              // сравнить, а 3.2 Вт/кг — это язык, на котором о мощности
              // говорят. Вес берём из профиля, дневниковый бывает пуст.
              value={
                sport.watts_per_kg != null
                  ? `${sport.ftp_watts} ${t("form_page.watts", { defaultValue: "Вт" })} · ${sport.watts_per_kg} ${t("form_page.w_per_kg", { defaultValue: "Вт/кг" })}`
                  : `${sport.ftp_watts} ${t("form_page.watts", { defaultValue: "Вт" })}`
              }
              // Возраст ПОРОГА, а не веса: Вт/кг стоят рядом со ваттами, и
              // свежесть веса подписана на своей плитке ниже — иначе в одной
              // строке было бы два разных срока без указания, чей какой.
              age={thresholdAge("ftp_watts")}
              to="/sport-profile/bike"
            />
          )}
          {sport?.critical_speed_sec_per_km != null && discMatches("run") && (
            <ThresholdRow
              testId="form-threshold-run"
              label={t("form_page.run", { defaultValue: "Бег" })}
              value={`${formatPaceSec(sport.critical_speed_sec_per_km)} ${t("form_page.per_km", { defaultValue: "/км" })}`}
              to="/sport-profile/run"
            />
          )}
          {sport?.lthr_bpm != null && (
            <ThresholdRow
              testId="form-threshold-lthr"
              label={t("form_page.lthr", { defaultValue: "Пульсовый порог" })}
              value={`${sport.lthr_bpm} ${t("form_page.bpm", { defaultValue: "уд/мин" })}`}
            />
          )}
          {/* VO₂max и VDOT приходили в этом же ответе и нигде не показывались.
              Для бегуна это две главные оценки потолка, и держать их только на
              экране вида спорта означало, что «Форма» умалчивает о том, что уже
              знает. */}
          {sport?.vo2max != null && (
            <ThresholdRow
              testId="form-threshold-vo2max"
              label={t("form_page.vo2max", { defaultValue: "VO₂max" })}
              value={`${sport.vo2max} ${t("form_page.ml_kg_min", { defaultValue: "мл/кг/мин" })}`}
              // Чьё это число — и спорят ли оценки. У Димы три источника дают
              // 44.1, 54.0 и 60.6: показать одно из них молча значит выдать
              // выбор за измерение, особенно рядом с VDOT (он считается от
              // результата и выглядит противоречием, хотя это другая величина).
              age={
                sport.vo2max_disagreement
                  ? t("form_page.vo2max_disagree", { defaultValue: "оценки расходятся" })
                  : sport.vo2max_source ?? undefined
              }
              to={studioLink("health", disc)}
            />
          )}
          {sport?.vdot != null && discMatches("run") && (
            <ThresholdRow
              testId="form-threshold-vdot"
              label={t("form_page.vdot", { defaultValue: "VDOT" })}
              value={String(sport.vdot)}
              to="/sport-profile/run"
            />
          )}
          <Link
            to="/zones"
            data-testid="form-zones-link"
            className="flex items-center justify-between px-3.5 py-3 hover:bg-bg-raised active:bg-bg-raised transition-colors"
          >
            <span className="text-[12.5px] text-fg-secondary">
              {t("form_page.zones", { defaultValue: "Мои зоны" })}
            </span>
            <CaretRight className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
          </Link>
        </Card>

        {/* Просроченный тест — активный сигнал, а не серая дата: пока порог
            не пересдан, все зоны считаются от устаревшего значения. */}
        {overdue.length > 0 && (
          <Link
            to="/threshold-test"
            data-testid="form-threshold-overdue"
            className="indigo-card mt-2 px-3.5 py-3 flex items-center gap-2 hover:bg-bg-raised active:bg-bg-raised transition-colors"
          >
            <Warning className="w-4 h-4 shrink-0 text-warn" aria-hidden />
            <span className="text-[12px] text-fg-secondary flex-1">
              {t("form_page.overdue", {
                defaultValue: "Пора перетестировать порог",
                count: overdue.length,
              })}
            </span>
            <CaretRight className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden />
          </Link>
        )}
      </section>

      {/* 5 · Тело — входы в разделы, которые описывают состояние глубже */}
      <section data-testid="form-body-links">
        <h2 className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted mb-2 px-0.5">
          {t("form_page.body", { defaultValue: "Тело" })}
        </h2>
        {/* СОСТОЯНИЕ, А НЕ ТОЛЬКО НАЗВАНИЕ — 2026-08-04.
            Четыре ссылки без единой цифры были единственным блоком экрана,
            который ничего не сообщал: чтобы узнать, есть ли открытая травма,
            надо было зайти и посмотреть. Приборная панель обязана отвечать,
            не открывая ящик.

            Где данных нет — там и подписи нет. «0 записей» под словом
            «Питание» не добавляет к нему ничего, кроме шума: у Димы пусты
            три раздела из четырёх, и три надписи «нет записей» сделали бы
            экран честнее, но не полезнее. Молчание здесь и есть ответ. */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { to: "/wellness", key: "form_page.wellness", fb: "Самочувствие", id: "form-link-wellness", state: wellnessState },
            { to: "/injuries", key: "form_page.injuries", fb: "Травмы", id: "form-link-injuries", state: injuryState },
            { to: "/nutrition-log", key: "form_page.nutrition", fb: "Питание", id: "form-link-nutrition", state: undefined },
            { to: "/mentality", key: "form_page.mentality", fb: "Ментальность", id: "form-link-mentality", state: undefined },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className="indigo-card px-3 py-2.5 flex flex-col gap-0.5 hover:bg-bg-raised active:bg-bg-raised transition-colors"
            >
              <span className="text-xs font-medium text-fg-secondary truncate">
                {t(l.key, { defaultValue: l.fb })}
              </span>
              {l.state && (
                <span className={`text-[10.5px] truncate ${l.state.tone === "warn" ? "text-warn" : "text-fg-muted"}`}>
                  {l.state.text}
                </span>
              )}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function ThresholdRow({
  label,
  value,
  testId,
  to,
  age,
}: {
  label: string;
  value: string;
  testId: string;
  to?: string;
  /**
   * Возраст порога и откуда он взялся. Порог без срока — порог неизвестной
   * давности, а от него считаются все зоны: у Димы FTP приехал из
   * intervals.icu 7 июня и к августу устарел на два месяца, но экран
   * показывал только «290 Вт», как будто это измерено сегодня.
   */
  age?: string;
}) {
  const inner = (
    <>
      <span className="text-[12.5px] text-fg-secondary">{label}</span>
      <span className="flex items-baseline gap-2">
        {age && <span className="text-[10.5px] text-fg-muted">{age}</span>}
        <span className="text-[13px] text-fg-primary num-display">{value}</span>
      </span>
    </>
  );
  return to ? (
    <Link
      to={to}
      data-testid={testId}
      // active: — тап-отклик: на мобиле hover не существует (образец ListView).
      className="flex items-center justify-between px-3.5 py-3 hover:bg-bg-raised active:bg-bg-raised transition-colors"
    >
      {inner}
    </Link>
  ) : (
    <div className="flex items-center justify-between px-3.5 py-3" data-testid={testId}>
      {inner}
    </div>
  );
}
